
import Vue from 'vue';
import Component from 'vue-class-component';
import {Prop} from 'vue-property-decorator';
import {loadGoogleMaps} from '@/helpers/routeDirectionsHelper';

export interface IPickerLocation {
  lat: number;
  lng: number;
  address: string;
}

@Component
export default class RouteLocationPickerMap extends Vue {
  @Prop({required: true}) protected mapsApiKey!: string;
  @Prop({default: 0})     protected initialLat!: number;
  @Prop({default: 0})     protected initialLng!: number;
  @Prop({default: ''})    protected initialAddress!: string;

  protected geocoding: boolean = false;
  protected error: string = '';
  protected addressSearch: string = '';
  protected location: IPickerLocation = {lat: 0, lng: 0, address: ''};

  private mapInstance: any = null;
  private markerInstance: any = null;
  private googleRef: any = null;

  mounted() {
    this.location = {
      lat:     this.initialLat || 0,
      lng:     this.initialLng || 0,
      address: this.initialAddress || '',
    };
    this.addressSearch = this.location.address;
    this.$nextTick(() => this.initMap());
  }

  private async initMap() {
    if (!this.mapsApiKey) {
      this.error = this.$t('routes.starting_locations.no_maps_key') as string;
      return;
    }

    try {
      const google = await loadGoogleMaps(this.mapsApiKey);
      this.googleRef = google;
      const container = this.$refs.mapContainer as HTMLElement;
      if (!container) { return; }

      const hasInitial = !!(this.initialLat && this.initialLng);
      this.mapInstance = new google.maps.Map(container, {
        center:            hasInitial ? {lat: this.initialLat, lng: this.initialLng} : {lat: 41.3879, lng: 2.16992},
        zoom:              hasInitial ? 15 : 12,
        mapTypeControl:    false,
        streetViewControl: false,
        fullscreenControl: false,
      });

      this.mapInstance.addListener('click', (event: any) => {
        this.placeMarker(event.latLng);
      });

      if (hasInitial) {
        this.placeMarker(new google.maps.LatLng(this.initialLat, this.initialLng), false);
      }
    } catch (e) {
      this.error = this.$t('routes.starting_locations.maps_error') as string;
    }
  }

  private placeMarker(latLng: any, reverseGeocode: boolean = true) {
    const google = this.googleRef;
    if (!google) { return; }

    if (this.markerInstance) { this.markerInstance.setMap(null); }

    this.markerInstance = new google.maps.Marker({
      position:  latLng,
      map:       this.mapInstance,
      animation: google.maps.Animation.DROP,
    });

    this.location = {...this.location, lat: latLng.lat(), lng: latLng.lng()};

    if (!reverseGeocode) {
      this.$emit('change', {...this.location});
      return;
    }

    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({location: {lat: latLng.lat(), lng: latLng.lng()}}, (results: any[], status: string) => {
      const address = (status === 'OK' && results && results[0])
        ? results[0].formatted_address
        : latLng.lat().toFixed(6) + ', ' + latLng.lng().toFixed(6);

      this.location      = {...this.location, address};
      this.addressSearch = address;
      this.$emit('change', {...this.location});
    });
  }

  protected async geocodeAddress() {
    if (!this.addressSearch || !this.mapInstance) { return; }
    this.geocoding = true;
    this.error     = '';

    try {
      const google = await loadGoogleMaps(this.mapsApiKey);
      this.googleRef = google;
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({address: this.addressSearch}, (results: any[], status: string) => {
        this.geocoding = false;
        if (status === 'OK' && results && results[0]) {
          const loc = results[0].geometry.location;
          this.location      = {...this.location, address: results[0].formatted_address};
          this.addressSearch = this.location.address;
          this.mapInstance.setCenter(loc);
          this.mapInstance.setZoom(15);
          this.placeMarker(loc, false);
        } else {
          this.error = this.$t('routes.starting_locations.geocode_error') as string;
        }
      });
    } catch (e) {
      this.geocoding = false;
      this.error     = this.$t('routes.starting_locations.geocode_error') as string;
    }
  }
}
